/**
 * Справка о земельном участке под точкой — с геопортала администрации.
 *
 * Лист генплана у нас растровый: по нему видно функциональную зону, но не
 * видно, какой именно участок под курсором и что с ним. Эти сведения есть на
 * геопортале администрации — там слой «Земельные участки» векторный и отвечает
 * по точке кадастровым номером, адресом, площадью и целевым использованием.
 *
 * Запрос идёт на наш же origin (/geoportal/parcel), а оттуда проксируется:
 * у geoportal.klgd.ru нет CORS-заголовков, поэтому прямой fetch со страницы
 * браузер заблокировал бы. Что именно уходит наружу, решает конфигурация
 * прокси — здесь задаётся только охват.
 *
 * Геометрии участка получить неоткуда: сервер отдаёт одни атрибуты. Поэтому
 * подсветить контур выбранного участка на карте нечем, и обещать этого в
 * интерфейсе не нужно.
 */

/** Полуобхват Земли по экватору в веб-Меркаторе. */
const HALF_EQUATOR = 20037508.342789244;

/**
 * Полусторона окна запроса в метрах проекции.
 *
 * Сервер отвечает по окну, а не по математической точке. Слишком узкое окно
 * промахивается мимо участка при клике по его краю, слишком широкое — ловит
 * соседей. Двадцать пять метров проекции — это около четырнадцати метров на
 * земле на широте Калининграда, то есть примерно толщина пальца на телефоне.
 */
const HALF_WINDOW_M = 25;

export interface ParcelInfo {
  /** Атрибуты как их вернул геопортал: состав полей задаёт он, не мы. */
  properties: Record<string, unknown>;
  /** Дата, на которую геопортал считает сведения актуальными. */
  sourceDate: string | null;
  cadastralNumber: string | null;
}

interface FeatureCollectionResponse {
  features?: { properties?: Record<string, unknown> | null }[];
}

function mercator(lng: number, lat: number): [number, number] {
  const x = (lng * HALF_EQUATOR) / 180;
  const y = (Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) * HALF_EQUATOR) / Math.PI;
  return [x, y];
}

/** Значения приходят из чужого JSON, поэтому тип проверяется, а не предполагается. */
function asText(value: unknown): string | null {
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value !== "string") return null;
  const text = value.trim();
  return text === "" ? null : text;
}

/**
 * Участок под точкой либо null, если там ничего не зарегистрировано.
 *
 * Сетевая ошибка тоже даёт null: справка вспомогательная, и ронять из-за неё
 * выбор объекта на карте незачем. Отменённый запрос пробрасывается наружу,
 * чтобы вызывающий код отличил отмену от пустого ответа.
 */
export async function queryParcel(
  lngLat: { lng: number; lat: number },
  signal?: AbortSignal,
): Promise<ParcelInfo | null> {
  const [x, y] = mercator(lngLat.lng, lngLat.lat);
  const bbox = [x - HALF_WINDOW_M, y - HALF_WINDOW_M, x + HALF_WINDOW_M, y + HALF_WINDOW_M].join(",");

  let payload: FeatureCollectionResponse;
  try {
    const response = await fetch(`/geoportal/parcel?bbox=${encodeURIComponent(bbox)}`, {
      signal: signal ?? null,
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    payload = (await response.json()) as FeatureCollectionResponse;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    console.warn("Геопортал не ответил про участок:", error);
    return null;
  }

  const properties = payload.features?.[0]?.properties;
  if (!properties) return null;

  return {
    properties,
    sourceDate: asText(properties["Дата источника данных"]),
    cadastralNumber: asText(properties["Кадастровый номер"]),
  };
}
